import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { salonTheme } from './theme/salonTheme';

import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import ClientDashboard from './pages/ClientDashboard';
import BookAppointment from './pages/BookAppointment';
import BookingConfirmation from './pages/BookingConfirmation';
import PaymentPage from './pages/PaymentPage';
import StaffRoster from './pages/StaffRoster';
import Specialties from './pages/Specialties';
import Appointments from './pages/Appointments';
import Calendar from './pages/Calendar';
import Clients from './pages/Clients';
import Staff from './pages/Staff';

import Payments from './pages/Payments';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import SystemLogs from './pages/SystemLogs';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import ProductAdmin from './pages/ProductAdmin';
import CalendarTest from './pages/CalendarTest';
import PublicBooking from './pages/PublicBooking';
import HomePage from './pages/HomePage';
import LoadingSpinner from './components/LoadingSpinner';
import LoyaltyRewards from './pages/LoyaltyRewards';
import { Box, Container, Typography, Button } from '@mui/material';

const App: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/book-appointment" element={
          <Box sx={{ 
            background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
            minHeight: '100vh',
            py: 2
          }}>
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" color="white">✨ Bella Vista Salon & Spa</Typography>
                <Box display="flex" gap={2}>
                  <Button color="inherit" onClick={() => window.location.href = '/login'} sx={{ color: 'white' }}>Login</Button>
                  <Button variant="outlined" color="inherit" onClick={() => window.location.href = '/signup'} sx={{ color: 'white', borderColor: 'white' }}>Sign Up</Button>
                </Box>
              </Box>
            </Container>
            <ThemeProvider theme={salonTheme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <BookAppointment />
              </LocalizationProvider>
            </ThemeProvider>
          </Box>
        } />
        <Route path="/payment" element={
          <ThemeProvider theme={salonTheme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <PaymentPage />
            </LocalizationProvider>
          </ThemeProvider>
        } />
        <Route path="/booking-confirmation" element={
          <ThemeProvider theme={salonTheme}>
            <BookingConfirmation />
          </ThemeProvider>
        } />
        <Route path="*" element={<HomePage />} />
      </Routes>
    );
  }

  return (
    <ThemeProvider theme={salonTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/*" element={
            <Layout>
              <Routes>
                <Route path="/dashboard" element={
          user.role === 'CLIENT' ? <ClientDashboard /> : <Dashboard />
        } />
        <Route path="/book-appointment" element={<BookAppointment />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/booking-confirmation" element={<BookingConfirmation />} />
        {(user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && (
          <Route path="/staff-roster" element={<StaffRoster />} />
        )}
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/calendar" element={<Calendar />} />
        {(user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && (
          <Route path="/clients" element={<Clients />} />
        )}
        {(user.role === 'ADMIN' || user.role === 'RECEPTIONIST' || user.role === 'STYLIST' || user.role === 'THERAPIST') && (
          <Route path="/staff-roster" element={<StaffRoster />} />
        )}
        {user.role === 'ADMIN' && (
          <>
            <Route path="/stylists" element={<Staff />} />
            <Route path="/services" element={<Specialties />} />
          </>
        )}
        <Route path="/payments" element={<Payments />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/calendar-test" element={<CalendarTest />} />
        <Route path="/calendar-test" element={<CalendarTest />} />
        {user.role === 'ADMIN' && (
          <>
            <Route path="/settings" element={<Settings />} />
            <Route path="/product-admin" element={<ProductAdmin />} />
            <Route path="/loyalty-rewards" element={<LoyaltyRewards />} />
            <Route path="/system-logs" element={<SystemLogs />} />
          </>
        )}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          } />
        </Routes>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
